import React from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Button } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const ExcelDownloader = ({
  sheets,
  disabled = true,
  fileName = "ReportData.xlsx",
}) => {
  const addSheet = ({
    workbook,
    sheetName,
    header = [],
    data = [],
    title,
    showHeader = true,
  }) => {
    const worksheet = workbook.addWorksheet(sheetName);
    let currentRow = 1;

    // ✅ TITLE ROW (Merged & Centered)
    if (title) {
      worksheet.addRow([title]);

      const colCount = header.length || 1;
      worksheet.mergeCells(currentRow, 1, currentRow, colCount);

      const cell = worksheet.getCell(currentRow, 1);
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.font = { bold: true };

      currentRow++;
    }

    // ✅ HEADER ROW (Input & Output only)
    if (showHeader && header.length > 0) {
      const headerRow = worksheet.addRow(
        header.map((col) => col.label || col.key),
      );

      // Make header row bold
      headerRow.eachCell((cell) => {
        cell.font = { bold: true };
      });

      currentRow++;
    }

    // ✅ DATA ROWS
    data.forEach((row) => {
      worksheet.addRow(
        header.map((col) => {
          let value = row[col.key];
          if (col.type === "boolean") return !!value;
          if (col.type === "int") return parseInt(value) || value;
          if (col.type === "float") return parseFloat(value) || value;
          return value ?? "";
        }),
      );
    });

    // ✅ AUTO WIDTH
    worksheet.columns.forEach((col) => {
      let maxLength = 10;
      col.eachCell({ includeEmpty: true }, (cell) => {
        const len = cell.value ? cell.value.toString().length : 0;
        maxLength = Math.max(maxLength, len);
      });
      col.width = maxLength + 2;
    });
  };

  const downloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();

    // ✅ MULTI-SHEET MODE
    if (Array.isArray(sheets) && sheets.length > 0) {
      sheets.forEach((sheet, index) => {
        addSheet({
          workbook,
          sheetName: sheet.name || `Sheet ${index + 1}`,
          header: sheet.header,
          data: sheet.data,
          title: sheet.title,
          showHeader: sheet.showHeader,
        });
      });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, fileName);
  };

  return (
    <Button
      variant="outlined"
      color="primary"
      size="medium"
      startIcon={<FileDownloadIcon />}
      onClick={downloadExcel}
      sx={{
        fontSize: "0.8rem",
        boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
        height: "40px",
      }}
      disabled={disabled}
    >
      Export to Excel
    </Button>
  );
};

export default ExcelDownloader;
